import { useCallback, useState, type FormEvent } from "react";
import { CheckCircle2 } from "lucide-react";
import {
  Banner,
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@alight/ui";
import { TurnstileWidget } from "./TurnstileWidget";
import {
  ApiError,
  submitMessage,
  TOPIC_LABELS,
  type MessageSource,
  type MessageTopic,
} from "../lib/api";

/**
 * The public "write to the Connect team" form — the Weebly form rebuilt:
 * name, email, a topic picker (enquiry / feedback / expression of interest /
 * other), the message, and the email-updates opt-in. Posts to
 * POST /api/messages and swaps to a success panel showing the reference.
 */

const EMAIL_RE = /^\S+@\S+\.\S+$/;

export interface MessageFormProps {
  source?: MessageSource;
  /** Label for the submit button. @default "Send message" */
  submitLabel?: string;
}

interface Values {
  name: string;
  email: string;
  topic: MessageTopic;
  message: string;
  updates: boolean;
  /** Honeypot — humans never see or fill this. */
  website: string;
}

const EMPTY: Values = { name: "", email: "", topic: "enquiry", message: "", updates: false, website: "" };

export function MessageForm({ source = "contact", submitLabel = "Send message" }: MessageFormProps) {
  const [values, setValues] = useState<Values>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof Values, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sentRef, setSentRef] = useState<string | null>(null);
  // Cloudflare's bot check. Empty until the widget reports a token.
  const [turnstileToken, setTurnstileToken] = useState("");
  const onVerify = useCallback((token: string) => setTurnstileToken(token), []);

  const idPrefix = `msg-${source}`;

  const set = <K extends keyof Values>(name: K, value: Values[K]) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);

    const errors: Partial<Record<keyof Values, string>> = {};
    if (values.name.trim() === "") errors.name = "Please tell us your name.";
    if (values.email.trim() === "") errors.email = "Please enter your email so we can reply.";
    else if (!EMAIL_RE.test(values.email.trim())) errors.email = "Please enter a valid email address.";
    if (values.message.trim() === "") errors.message = "Please write a message.";
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitMessage({
        ...(turnstileToken !== "" ? { turnstileToken } : {}),
        source,
        topic: values.topic,
        name: values.name.trim(),
        email: values.email.trim(),
        subject: TOPIC_LABELS[values.topic],
        body: values.message.trim(),
        wants_updates: values.updates,
        // Honeypot travels as-is; the Worker silently drops filled ones.
        ...(values.website !== "" ? { website: values.website } : {}),
      });
      setSentRef(result.ref);
      setValues(EMPTY);
      setTurnstileToken("");
    } catch (err) {
      setFormError(
        err instanceof ApiError && err.status > 0
          ? `We couldn't send your message just now — ${err.message}. Please try again, or email us directly.`
          : "Something went wrong sending your message. Please check your connection and try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (sentRef) {
    return (
      <div role="status" className="rounded-card border border-success/30 bg-success/10 p-6 text-center">
        <CheckCircle2 className="mx-auto h-10 w-10 text-success" aria-hidden />
        <h3 className="mt-3 font-heading text-xl font-bold text-ink">Thanks — your message is on its way!</h3>
        <p className="mt-2 text-ink">
          Your reference is <span className="font-mono font-semibold text-ink">{sentRef}</span>.
        </p>
        <p className="mt-1 text-sm text-muted">Someone from the Connect team will reply by email.</p>
        <Button type="button" variant="outline" size="sm" className="mt-4" onClick={() => setSentRef(null)}>
          Send another message
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      {/* Honeypot — visually hidden and skipped by keyboard/screen readers. */}
      <div className="sr-only" aria-hidden="true">
        <label htmlFor={`${idPrefix}-website`}>Website</label>
        <input
          id={`${idPrefix}-website`}
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={(e) => set("website", e.target.value)}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-name`}>Name</Label>
          <Input
            id={`${idPrefix}-name`}
            name="name"
            autoComplete="name"
            required
            value={values.name}
            onChange={(e) => set("name", e.target.value)}
            disabled={submitting}
            aria-invalid={fieldErrors.name ? true : undefined}
            aria-describedby={fieldErrors.name ? `${idPrefix}-name-error` : undefined}
          />
          {fieldErrors.name && (
            <p id={`${idPrefix}-name-error`} className="text-sm text-error">
              {fieldErrors.name}
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-email`}>Email</Label>
          <Input
            id={`${idPrefix}-email`}
            name="email"
            type="email"
            autoComplete="email"
            required
            value={values.email}
            onChange={(e) => set("email", e.target.value)}
            disabled={submitting}
            aria-invalid={fieldErrors.email ? true : undefined}
            aria-describedby={fieldErrors.email ? `${idPrefix}-email-error` : undefined}
          />
          {fieldErrors.email && (
            <p id={`${idPrefix}-email-error`} className="text-sm text-error">
              {fieldErrors.email}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-topic`}>What is this about?</Label>
        <Select value={values.topic} onValueChange={(v) => set("topic", v as MessageTopic)} disabled={submitting}>
          <SelectTrigger id={`${idPrefix}-topic`} className="w-full sm:max-w-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(TOPIC_LABELS) as MessageTopic[]).map((t) => (
              <SelectItem key={t} value={t}>
                {TOPIC_LABELS[t]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-message`}>Message</Label>
        <Textarea
          id={`${idPrefix}-message`}
          name="message"
          rows={5}
          required
          value={values.message}
          onChange={(e) => set("message", e.target.value)}
          disabled={submitting}
          aria-invalid={fieldErrors.message ? true : undefined}
          aria-describedby={fieldErrors.message ? `${idPrefix}-message-error` : undefined}
        />
        {fieldErrors.message && (
          <p id={`${idPrefix}-message-error`} className="text-sm text-error">
            {fieldErrors.message}
          </p>
        )}
      </div>

      <div className="flex items-start gap-3">
        <Checkbox
          id={`${idPrefix}-updates`}
          checked={values.updates}
          onCheckedChange={(v) => set("updates", v === true)}
          disabled={submitting}
        />
        <Label htmlFor={`${idPrefix}-updates`} className="font-normal leading-snug">
          Yes — email me updates about Connect
        </Label>
      </div>

      {formError && <Banner variant="error">{formError}</Banner>}

      <TurnstileWidget onVerify={onVerify} />

      <Button type="submit" disabled={submitting}>
        {submitting ? "Sending…" : submitLabel}
      </Button>
    </form>
  );
}
